import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilState } from 'recoil'

import styled from 'styled-components'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import { getConfig } from 'core/singleton'
import ButtonBack from 'components/Button/ButtonBack'
import SearchInput from 'components/Input/SearchInput'
import ButtonSearch from 'components/Button/ButtonSearch'
import PageHeader from 'components/Page/PageHeader'
import PageContent from 'components/Page/PageContent'
import ButtonDeleteAll from 'components/Button/ButtonDeleteAll'
import ButtonDelete from 'components/Button/ButtonDelete'
import { encodePercent } from 'core/util/search'
import { useTranslation } from 'react-i18next'
import { KEYWORD_UPDATE } from 'core/store/search'
import { STORE_ROOT_PROPS } from 'core/store/global'
import { useRecoilValue } from 'recoil'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { getLocation } from 'core/util/default'

interface PageSearchProps {}

const PageSearch: React.FC<PageSearchProps> = (props) => {
  const { t } = useTranslation()
  const [keywords, setKeywords] = useRecoilState(KEYWORD_UPDATE)
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const { logo, logoAltText, customSearchBar } = getConfig()
  const [input, setInput] = useState<string>('')
  const history = useHistory()
  const location = useLocation()

  /**
   * @name EventHandler
   **/
  const handleClickBack = useCallback(() => {
    new GAHelper().sendEvent(GA_EVENT.CLICK_BACK, {
      partnersId: root?.partnerId,
      location: getLocation(location),
    })

    history.goBack()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  const handleClickClearKeywords = useCallback(() => {
    setKeywords([])
    new GAHelper().sendEvent(GA_EVENT.CLICK_CLEAR_ALL, {
      partnersId: root?.partnerId,
      location: getLocation(location),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setKeywords])

  const handleClickDeleteKeyword = useCallback(
    (keyword: string) => () => {
      const filteredKeywords = keywords.filter((x) => x !== keyword)
      setKeywords(filteredKeywords)

      new GAHelper().sendEvent(GA_EVENT.CLICK_RECENT_KWD_CLEAR, {
        partnersId: root?.partnerId,
        location: getLocation(location),
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setKeywords, keywords],
  )

  const handleChangeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getSearchUrl = useCallback(
    (keyword: string) => {
      const encodePercentKeyword = encodePercent(keyword)
      return `/search/${encodeURIComponent(encodePercentKeyword)}${location.search}`
    },
    [location],
  )

  const historyPushUrl = useCallback(
    (keyword: string) => {
      history.push(getSearchUrl(keyword))
    },
    [history, getSearchUrl],
  )

  const handleSearchKeyword = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const keyword = input

      if (keyword) {
        const index = keywords.indexOf(keyword)
        if (index < 0) {
          setKeywords([keyword, ...keywords])
        } else {
          const _keywords = keywords.filter((x) => keyword !== x)
          setKeywords([keyword, ..._keywords])
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_SEARCH, {
          partnersId: root?.partnerId,
          location: getLocation(location),
          keyword: keyword,
        })
        historyPushUrl(keyword)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keywords, setKeywords, input, historyPushUrl],
  )

  const handleClickSearchDelete = useCallback(() => {
    setInput('')
    new GAHelper().sendEvent(GA_EVENT.CLICK_CLEAR, {
      partnersId: root?.partnerId,
      location: getLocation(location),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const initKeywords = JSON.parse(localStorage.getItem('sauceFlexCollect') || '[]')
    if (JSON.stringify(keywords) === JSON.stringify(initKeywords)) return
    setKeywords(initKeywords)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setKeywords])

  const keywordListEl = useMemo(() => {
    return keywords.map((keyword) => (
      <div className="keyword" key={keyword}>
        <NavLink
          to={getSearchUrl(keyword)}
          onClick={() =>
            new GAHelper().sendEvent(GA_EVENT.CLICK_RECENT_KWD, {
              partnersId: root?.partnerId,
              location: getLocation(location),
              keyword: keyword,
            })
          }
          className="keyword_item"
        >
          {keyword}
        </NavLink>
        <ButtonDelete onClick={handleClickDeleteKeyword(keyword)} />
      </div>
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywords, getSearchUrl])

  const partnerLogoElement = useMemo(() => {
    if (logo && /^https:\/\//gi.test(logo)) {
      return <img src={logo} alt={logoAltText} />
    }
    return <div className="text">{logo}</div>
  }, [logo, logoAltText])

  return (
    <StyledPageSearch>
      {/* 모바일 */}
      <div className="search mobile">
        <PageHeader border onSubmit={handleSearchKeyword} id="sf_searchStart">
          <ButtonBack onClick={handleClickBack} />
          <SearchInput onChange={handleChangeInput} value={input} placeholder={t('search_item')} />
          {input.length > 0 && <ButtonDelete onClick={handleClickSearchDelete} />}
          <ButtonSearch customSearchBar={customSearchBar} />
        </PageHeader>
        <PageContent className="page_search">
          <div className="page_search_header">
            <div className="search_title" tabIndex={0}>
              {t('search_recent')}
            </div>
            <ButtonDeleteAll onClick={handleClickClearKeywords}>전체삭제</ButtonDeleteAll>
          </div>
          <div className="keyword_list">{keywordListEl}</div>
        </PageContent>
      </div>
      {/* 데스크탑 */}
      <div className="desktop">
        <PageHeader>
          <div className="partner_logo" tabIndex={0}>
            {partnerLogoElement}
          </div>
          <ButtonBack close onClick={handleClickBack} />
        </PageHeader>
        <PageContent className="search">
          <PageHeader border onSubmit={handleSearchKeyword} id="sf_searchStart">
            <ButtonBack onClick={handleClickBack} />
            <SearchInput
              onChange={handleChangeInput}
              value={input}
              placeholder={t('search_item')}
            />
            {input.length > 0 && <ButtonDelete onClick={handleClickSearchDelete} />}
            <ButtonSearch customSearchBar={customSearchBar} />
          </PageHeader>
          <PageContent className="page_search">
            <div className="page_search_header">
              <div className="search_title" tabIndex={0}>
                {t('search_recent')}
              </div>
              <ButtonDeleteAll onClick={handleClickClearKeywords}>
                {t('delete_all')}
              </ButtonDeleteAll>
            </div>
            <div className="keyword_list">{keywordListEl}</div>
          </PageContent>
        </PageContent>
      </div>
    </StyledPageSearch>
  )
}

const StyledPageSearch = styled.div`
  .search {
    .page_search_header {
      height: 44px;
      padding: 12px 0px 8px 16px;
      display: flex;
      width: 100%;
      justify-content: space-between;
    }
    .search_title {
      color: ${(props) => props.theme.font.primary};
      font-weight: 700;
      font-size: 20px;
      line-height: 24px;
    }
    .keyword_list {
      padding: 12px 0px 8px 16px;
      width: 100%;
    }
    .keyword {
      height: 40px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      display: none;
      :nth-child(1),
      :nth-child(2),
      :nth-child(3),
      :nth-child(4),
      :nth-child(5) {
        display: flex;
      }
      .keyword_item {
        display: block;
        flex: 1;
        height: 100%;
        line-height: 40px;
        cursor: pointer;
      }
    }
  }
  .desktop {
    .search {
      width: 769px;
      height: 500px;
      margin: 0 auto;
      margin-top: 120px;
      display: flex;
      flex-direction: column;
    }
    .keyword {
      display: none;
      :nth-child(1),
      :nth-child(2),
      :nth-child(3),
      :nth-child(4),
      :nth-child(5) {
        display: flex;
      }
    }
  }

  .partner_logo > img {
    width: 150px;
    height: 50px;
  }

  @media (max-width: 768px) {
    .mobile {
      display: block;
    }
    .desktop {
      display: none;
    }
  }
  @media (min-width: 769px) {
    .mobile {
      display: none;
    }
    .desktop {
      display: block;
    }
  }
`
export default PageSearch
